import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { Handshake, Plus } from 'lucide-react';
import { CONSIGNMENT_PERMS } from '@mlm/shared';
import { listConsignmentCustomers } from '../../lib/api';
import { useHasPerm } from '../../lib/auth-store';

const peso = (v: string | number) =>
  `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Consignment landing page: every customer who holds consigned stock or owes
 * on a billing, with the numbers the demo leads with — units still in their
 * hands and the receivable balance.
 */
export function ConsignmentCustomersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  // Debounce: only refetch when the user pauses typing.
  const canConsignOut = useHasPerm(CONSIGNMENT_PERMS.consignOut);
  const [q, setQ] = useState('');
  const query = useQuery({
    queryKey: ['consignment-customers', q],
    queryFn: () => listConsignmentCustomers(q || undefined),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Consignment</h1>
          <p className="text-sm text-muted-foreground">
            Stock consigned out, billings, and collections per consignee.
          </p>
        </div>
        {canConsignOut && (
          <Button onClick={() => navigate('/consignment/new')}>
            <Plus className="mr-1 h-4 w-4" /> Consign Out
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">Consignees</CardTitle>
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                window.setTimeout(() => setQ(e.target.value), 300);
              }}
              placeholder="Search name or email…"
              className="w-64"
            />
          </div>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : (query.data ?? []).length === 0 ? (
            <div className="py-10 text-center">
              <Handshake className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm font-medium">No consignees yet</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                Consign stock out to a consignee, record their sales as billings, receive payments
                per billing, and print their statement of account.
              </p>
              {canConsignOut && (
                <Button className="mt-4" onClick={() => navigate('/consignment/new')}>
                  Consign Out
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-3">Consignee</th>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3 text-right">Units out</th>
                    <th className="py-2 pr-3 text-right">Open billings</th>
                    <th className="py-2 pr-3 text-right">Receivable</th>
                    <th className="py-2">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {(query.data ?? []).map((row) => (
                    <tr
                      key={row.customer_user_id}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/50"
                      onClick={() => navigate(`/consignment/${row.customer_user_id}`)}
                    >
                      <td className="py-2.5 pr-3 font-medium">{row.customer_name}</td>
                      <td className="py-2.5 pr-3 text-muted-foreground">{row.email}</td>
                      <td className="py-2.5 pr-3 text-right">{row.units}</td>
                      <td className="py-2.5 pr-3 text-right">{row.open_billings}</td>
                      <td className="py-2.5 pr-3 text-right font-medium">{peso(row.receivable)}</td>
                      <td className="py-2.5 text-muted-foreground">
                        {row.last_activity ? new Date(row.last_activity).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
